/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC boundary; see mem://preferences/lint-debt.md */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BUCKET_ORDER,
  type CorpusBucket,
  type CorpusDocumentRow,
  type CorpusFolder,
  createDocument,
  createFolder,
  deleteDocument,
  deleteFolder,
  getDocumentBody,
  invokeCorpusEmbed,
  invokeCorpusEmbedBatch,
  listDocuments,
  listDocumentTopics,
  listFolders,
  renameFolder,
  reorderDocument,
  reorderFolder,
  setCorpusEmbedErrorHandler,
  setDocumentTopics,
  setFolderExcludeFromGrounding,
  updateDocument,
} from "@/lib/workspaceCorpus";
import { supabase } from "@/integrations/supabase/client";

// Module-level flag so the "background embedding unavailable" toast only
// fires once per session, no matter how many docs the user saves.
let corpusEmbedToastShown = false;
import { hasAnyTaggedDocument, listPublishTypes, type PublishType } from "@/lib/workspacePublishTypes";
import type { FolderNode } from "./CorpusFolderAccordion";

export type Selection =
  | { kind: "folder"; folderId: string | null; bucket: CorpusBucket | null }
  | { kind: "unfiled" };

const buildTree = (
  folders: CorpusFolder[],
  docs: CorpusDocumentRow[],
  bucket: CorpusBucket
): FolderNode[] => {
  const inBucket = folders.filter((f) => f.bucket === bucket);
  const byParent = new Map<string | null, CorpusFolder[]>();
  for (const f of inBucket) {
    const k = f.parent_folder_id;
    if (!byParent.has(k)) byParent.set(k, []);
    byParent.get(k)!.push(f);
  }
  const docCount = (folderId: string) =>
    docs.filter((d) => d.folder_id === folderId).length;
  const build = (parent: string | null): FolderNode[] =>
    (byParent.get(parent) ?? []).map((f) => ({
      ...f,
      children: build(f.id),
      docCount: docCount(f.id),
    }));
  return build(null);
};

export type CorpusFolderDialogState = {
  open: boolean;
  bucket: CorpusBucket;
  parentId: string | null;
  parentName?: string;
};

export const useCorpusDock = (workspaceId: string | undefined) => {
  const [folders, setFolders] = useState<CorpusFolder[]>([]);
  const [docs, setDocs] = useState<CorpusDocumentRow[]>([]);
  const [publishTypes, setPublishTypes] = useState<PublishType[]>([]);
  const [anyTagged, setAnyTagged] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState<Selection>({
    kind: "folder",
    folderId: null,
    bucket: "primary",
  });

  /** Map of documentId → array of framework_topic_ids. */
  const [docTopics, setDocTopics] = useState<Record<string, string[]>>({});
  /** Map of framework_topic_id → { label, column_id, color_key }. */
  const [topicMeta, setTopicMeta] = useState<
    Record<string, { label: string; framework_column_id: string; color_key: string | null }>
  >({});
  /** Active Topic filter chips (framework_topic_ids). OR semantics. */
  const [topicFilter, setTopicFilter] = useState<string[]>([]);

  const [editingDocId, setEditingDocId] = useState<string | "new" | null>(null);
  const [editorTitle, setEditorTitle] = useState("");
  const [editorBody, setEditorBody] = useState("");
  const [editorTopicIds, setEditorTopicIds] = useState<string[]>([]);
  const [editorBusy, setEditorBusy] = useState(false);
  /** Body as loaded from the server, used to detect no-op saves so we
   *  don't mark the document stale (and trigger a re-embed) when only
   *  metadata like Topics changed. */
  const [loadedBody, setLoadedBody] = useState<string>("");

  const [folderDialog, setFolderDialog] = useState<CorpusFolderDialogState>({
    open: false,
    bucket: "primary",
    parentId: null,
  });
  const [folderDialogName, setFolderDialogName] = useState("");

  const [renameDialog, setRenameDialog] = useState<{ open: boolean; folder: CorpusFolder | null }>(
    { open: false, folder: null }
  );
  const [renameValue, setRenameValue] = useState("");

  const refreshTopicMeta = async () => {
    if (!workspaceId) return;
    const [tRes, cRes] = await Promise.all([
      (supabase as any)
        .from("content_framework_topics")
        .select("id, label, framework_column_id")
        .eq("workspace_id", workspaceId),
      (supabase as any)
        .from("content_framework_columns")
        .select("id, color_key")
        .eq("workspace_id", workspaceId),
    ]);
    const colorByCol = new Map<string, string | null>();
    for (const c of (cRes.data ?? []) as { id: string; color_key: string | null }[]) {
      colorByCol.set(c.id, c.color_key ?? null);
    }
    const meta: Record<string, { label: string; framework_column_id: string; color_key: string | null }> = {};
    for (const t of (tRes.data ?? []) as { id: string; label: string; framework_column_id: string }[]) {
      meta[t.id] = {
        label: t.label,
        framework_column_id: t.framework_column_id,
        color_key: colorByCol.get(t.framework_column_id) ?? null,
      };
    }
    setTopicMeta(meta);
  };

  const refresh = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [f, d, pt, tagged, dt] = await Promise.all([
        listFolders(workspaceId),
        listDocuments(workspaceId),
        listPublishTypes(workspaceId),
        hasAnyTaggedDocument(workspaceId),
        listDocumentTopics(workspaceId),
      ]);
      setFolders(f);
      setDocs(d);
      setPublishTypes(pt);
      setAnyTagged(tagged);
      setDocTopics(dt);
      void refreshTopicMeta();
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to load corpus");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    setCorpusEmbedErrorHandler(() => {
      if (corpusEmbedToastShown) return;
      corpusEmbedToastShown = true;
      toast.error(
        "Saved, but background indexing is unavailable right now. The document is stored — AI search will catch up once embeddings are re-enabled.",
        { duration: 8000 }
      );
    });
    return () => setCorpusEmbedErrorHandler(null);
  }, []);

  const handleToggleGrounding = async (folder: CorpusFolder, exclude: boolean) => {
    try {
      await setFolderExcludeFromGrounding(folder.id, exclude);
      setFolders((prev) =>
        prev.map((f) => (f.id === folder.id ? { ...f, exclude_from_grounding: exclude } : f))
      );
      toast.success(exclude ? "Folder excluded from AI grounding" : "Folder included in AI grounding");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to update folder");
    }
  };

  const handleReembed = async (docId: string) => {
    try {
      setDocs((prev) =>
        prev.map((d) => (d.id === docId ? { ...d, embedding_status: "processing" as const } : d))
      );
      await invokeCorpusEmbed(docId);
      await refresh();
      toast.success("Re-embedded");
    } catch (err: any) {
      toast.error(err?.message ?? "Re-embed failed");
    }
  };

  const handleReembedStale = async () => {
    if (!workspaceId) return;
    try {
      await invokeCorpusEmbedBatch(workspaceId, "stale");
      await refresh();
      toast.success("Re-embed queued");
    } catch (err: any) {
      toast.error(err?.message ?? "Batch re-embed failed");
    }
  };

  const trees = useMemo(
    () => Object.fromEntries(BUCKET_ORDER.map((b) => [b, buildTree(folders, docs, b)])),
    [folders, docs]
  ) as Record<CorpusBucket, FolderNode[]>;

  const visibleDocs = useMemo(() => {
    let list: CorpusDocumentRow[];
    if (selection.kind === "unfiled") {
      list = docs.filter((d) => d.folder_id === null);
    } else if (selection.folderId === null) {
      const folderIdsInBucket = new Set(
        folders.filter((f) => f.bucket === selection.bucket).map((f) => f.id)
      );
      list = docs.filter((d) => d.folder_id !== null && folderIdsInBucket.has(d.folder_id));
    } else {
      list = docs.filter((d) => d.folder_id === selection.folderId);
    }
    if (topicFilter.length > 0) {
      const want = new Set(topicFilter);
      list = list.filter((d) => {
        const tids = docTopics[d.id] ?? [];
        return tids.some((t) => want.has(t));
      });
    }
    return list;
  }, [docs, folders, selection, topicFilter, docTopics]);

  const startNewDoc = () => {
    setEditingDocId("new");
    setEditorTitle("");
    setEditorBody("");
    setEditorTopicIds([]);
    setLoadedBody("");
  };

  const openExistingDoc = async (doc: CorpusDocumentRow) => {
    setEditingDocId(doc.id);
    setEditorTitle(doc.title);
    setEditorBody("");
    setEditorTopicIds(docTopics[doc.id] ?? []);
    setLoadedBody("");
    try {
      const body = await getDocumentBody(doc.id);
      setEditorBody(body);
      setLoadedBody(body);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to load document");
    }
  };

  const saveEditor = async (title: string, body: string, topicIds: string[]) => {
    if (!workspaceId) return;
    setEditorBusy(true);
    try {
      let savedId: string | null = null;
      if (editingDocId === "new") {
        const targetFolderId = selection.kind === "folder" ? selection.folderId : null;
        const row = await createDocument({
          workspaceId,
          folderId: targetFolderId,
          title,
          body,
          sourceKind: "manual",
        });
        savedId = row.id;
      } else if (editingDocId) {
        // Skip body write (and the stale-mark it triggers) when the body
        // hasn't actually changed since we loaded it. Topic-only edits
        // therefore never cause a re-embed.
        const bodyChanged = body !== loadedBody;
        await updateDocument({
          id: editingDocId,
          title,
          body: bodyChanged ? body : undefined,
        });
        savedId = editingDocId;
      }
      if (savedId) {
        try {
          await setDocumentTopics(savedId, workspaceId, topicIds);
        } catch (err: any) {
          toast.error(err?.message ?? "Could not save topic assignments");
        }
      }
      setEditingDocId(null);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? "Save failed");
    } finally {
      setEditorBusy(false);
    }
  };

  /**
   * Inline row-level topic assignment. Writes only to the join table —
   * never touches the document body, so `embedding_status` is never
   * marked stale.
   */
  const setRowTopics = async (documentId: string, topicIds: string[]) => {
    if (!workspaceId) return;
    const prev = docTopics[documentId] ?? [];
    setDocTopics((m) => ({ ...m, [documentId]: topicIds }));
    try {
      await setDocumentTopics(documentId, workspaceId, topicIds);
    } catch (err: any) {
      setDocTopics((m) => ({ ...m, [documentId]: prev }));
      toast.error(err?.message ?? "Could not save topic assignments");
    }
  };

  const handleDeleteDoc = async (doc: CorpusDocumentRow) => {
    if (!confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await deleteDocument(doc.id);
      if (editingDocId === doc.id) setEditingDocId(null);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? "Delete failed");
    }
  };

  const openCreateFolder = (bucket: CorpusBucket, parent?: CorpusFolder) => {
    setFolderDialog({
      open: true,
      bucket,
      parentId: parent?.id ?? null,
      parentName: parent?.name,
    });
    setFolderDialogName("");
  };

  const submitCreateFolder = async () => {
    if (!workspaceId) return;
    try {
      await createFolder({
        workspaceId,
        bucket: folderDialog.bucket,
        name: folderDialogName,
        parentFolderId: folderDialog.parentId,
      });
      setFolderDialog((s) => ({ ...s, open: false }));
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to create folder");
    }
  };

  const submitRename = async () => {
    if (!renameDialog.folder) return;
    try {
      await renameFolder(renameDialog.folder.id, renameValue);
      setRenameDialog({ open: false, folder: null });
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? "Rename failed");
    }
  };

  const handleDeleteFolder = async (folder: CorpusFolder) => {
    if (
      !confirm(
        `Delete folder "${folder.name}"? Sub-folders will be removed; documents inside will move to Unfiled.`
      )
    )
      return;
    try {
      await deleteFolder(folder.id);
      if (selection.kind === "folder" && selection.folderId === folder.id) {
        setSelection({ kind: "folder", folderId: null, bucket: folder.bucket });
      }
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? "Delete failed");
    }
  };

  const staleCount = useMemo(
    () => docs.filter((d) => d.embedding_status === "stale").length,
    [docs]
  );

  // Build set of descendants for a folder so DnD can refuse self/descendant drops.
  const descendantsOf = (folderId: string): Set<string> => {
    const out = new Set<string>();
    const walk = (id: string) => {
      out.add(id);
      for (const f of folders) {
        if (f.parent_folder_id === id) walk(f.id);
      }
    };
    walk(folderId);
    return out;
  };

  const handleReorderFolder = async (input: {
    folderId: string;
    newParentId: string | null;
    bucket: CorpusBucket;
    newIndex: number;
  }) => {
    if (!workspaceId) return;
    const folder = folders.find((f) => f.id === input.folderId);
    if (!folder) return;
    if (folder.bucket !== input.bucket) {
      toast.error("Folders can't move between Primary, Secondary, or Supplemental.");
      return;
    }
    if (input.newParentId) {
      const blocked = descendantsOf(input.folderId);
      if (blocked.has(input.newParentId)) {
        toast.error("Can't drop a folder into itself or one of its sub-folders.");
        return;
      }
      const parent = folders.find((f) => f.id === input.newParentId);
      if (parent && parent.bucket !== input.bucket) {
        toast.error("Folders can't move between Primary, Secondary, or Supplemental.");
        return;
      }
    }
    try {
      await reorderFolder({ ...input, workspaceId });
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? "Reorder failed");
    }
  };

  const handleReorderDocument = async (input: {
    documentId: string;
    newFolderId: string | null;
    newIndex: number;
  }) => {
    if (!workspaceId) return;
    try {
      await reorderDocument({ ...input, workspaceId });
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? "Move failed");
    }
  };

  const editingDoc =
    editingDocId && editingDocId !== "new"
      ? docs.find((d) => d.id === editingDocId) ?? null
      : null;

  return {
    // state
    folders,
    docs,
    publishTypes,
    setPublishTypes,
    anyTagged,
    loading,
    selection,
    setSelection,
    editingDocId,
    setEditingDocId,
    editorTitle,
    editorBody,
    editorTopicIds,
    editorBusy,
    docTopics,
    topicMeta,
    topicFilter,
    setTopicFilter,
    folderDialog,
    setFolderDialog,
    folderDialogName,
    setFolderDialogName,
    renameDialog,
    setRenameDialog,
    renameValue,
    setRenameValue,
    // derived
    trees,
    visibleDocs,
    staleCount,
    editingDoc,
    // actions
    refresh,
    handleToggleGrounding,
    handleReembed,
    handleReembedStale,
    startNewDoc,
    openExistingDoc,
    saveEditor,
    setRowTopics,
    handleDeleteDoc,
    openCreateFolder,
    submitCreateFolder,
    submitRename,
    handleDeleteFolder,
    handleReorderFolder,
    handleReorderDocument,
  };
};
